/**
 * Entity workbench — Contacts tab (ENTITY_WORKBENCH_PLAN.md W8).
 *
 * Three populations, one screen:
 *   Panel 0 · directors and officers (key_role_bindings folded per person × role —
 *     W6, surfaced here because these are the people an operator opens
 *     Contacts to see). Current holders and closed seats with successor.
 *   Panel A · organisational channels (entity_contacts) — an inbox belongs to
 *     a company, so Assayer owns it outright.
 *   Panel B · people — aggregated from extraction, keyed on lower(email)
 *     (R16). Third parties are legible because the extraction carries the
 *     person's OWN organisation: a Read Corporate address on an AQN filing
 *     shows as Read Corporate.
 *
 * ACCOUNTABLE surface: provenance, confidence and as-at stamps are shown, not
 * hidden. Approval writes the same recipient-review ledger that gates sends —
 * one ledger, two doors.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  AtSign, Building2, Check, ChevronRight, Loader2, Mail, Phone, Plus,
  Trash2, Users, X,
} from 'lucide-react';
import {
  addEntityChannel, deleteEntityChannel, getEntityContacts, setPersonApproval,
  updateEntityChannel,
  type ContactChannel, type ContactPerson, type KeyRoleHolding, type PersonApprovalAction,
} from '../../lib/api/entity-workbench.js';
import { TableScroll } from '../ui/table-scroll.js';
import { PersonDrawer } from './PersonDrawer.js';

const CHANNEL_TYPES = [
  'investor_relations', 'media', 'executive_office', 'generic_inbox', 'general',
];

const CHANNEL_TYPE_LABEL: Record<string, string> = {
  investor_relations: 'Investor relations',
  media: 'Media',
  executive_office: 'Executive office',
  generic_inbox: 'Generic inbox',
  general: 'General',
};

const ROLE_LABEL: Record<string, string> = {
  media_contact: 'Media contact',
  pr_representative: 'PR representative',
  investor_relations: 'Investor relations',
  analyst: 'Analyst',
  executive: 'Executive',
  board_member: 'Board member',
  advisor: 'Advisor',
};

const PROVENANCE_LABEL: Record<string, string> = {
  operator_added: 'operator',
  prism_promotion: 'extracted',
  prism_promotion_backfill: 'extracted (backfill)',
  extraction: 'extracted',
  backfill: 'backfill',
};

const KEY_ROLE_LABEL: Record<string, string> = {
  chair: 'Chair',
  vice_chair: 'Vice chair',
  lead_independent_director: 'Lead independent director',
  md_ceo: 'MD & CEO',
  ceo: 'CEO',
  md: 'Managing director',
  cfo: 'CFO',
  coo: 'COO',
  cto: 'CTO',
  executive_director: 'Executive director',
  non_executive_director: 'Non-executive director',
  company_secretary: 'Company secretary',
  auditor: 'Auditor',
  other: 'Other',
};

type PeopleFilter = 'active' | 'pending' | 'approved' | 'all';
type RolesFilter = 'current' | 'former' | 'all';

function fmt(d: string | null | undefined, pattern = 'd MMM yyyy') {
  return d ? format(new Date(d), pattern) : '—';
}

export function ContactsTab({ entityId }: { entityId: string }) {
  const queryClient = useQueryClient();
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>('active');
  const [rolesFilter, setRolesFilter] = useState<RolesFilter>('current');

  const contactsQuery = useQuery({
    queryKey: ['entity-contacts', entityId],
    queryFn: () => getEntityContacts(entityId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['entity-contacts', entityId] });
  };

  if (contactsQuery.isLoading) {
    return <div className="py-16 text-center text-sm text-grey-500">Loading contacts…</div>;
  }
  if (contactsQuery.isError || !contactsQuery.data) {
    return <div className="py-16 text-center text-sm text-red-600">Could not load contacts.</div>;
  }

  const { channels, people, key_roles } = contactsQuery.data;

  return (
    <div className="space-y-4">
      <KeyRolesPanel keyRoles={key_roles} filter={rolesFilter} onFilter={setRolesFilter} />
      <ChannelsPanel entityId={entityId} channels={channels} onChanged={invalidate} />
      <PeoplePanel
        entityId={entityId}
        people={people}
        filter={peopleFilter}
        onFilter={setPeopleFilter}
        onOpenPerson={setOpenPerson}
        onChanged={invalidate}
      />
      {openPerson && (
        <PersonDrawer
          email={openPerson}
          contextEntityId={entityId}
          onClose={() => setOpenPerson(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel 0 · directors and officers (key_role_bindings)
// ---------------------------------------------------------------------------

interface KeyRolePerson {
  person_key: string;
  person_name: string;
  person_entity_id: string | null;
  shared_person_id: boolean;
  confidence: string | null;
  is_current: boolean;
  seats: KeyRoleHolding[];
}

/** Holdings (one per person × seat) → one entry per person, seats nested.
 *  Grouped on person_key, which already carries the backend's split of a
 *  mis-bound shared id; backend order is kept (current first). */
function groupByPerson(holdings: KeyRoleHolding[]): KeyRolePerson[] {
  const by = new Map<string, KeyRolePerson>();
  for (const h of holdings) {
    let p = by.get(h.person_key);
    if (!p) {
      p = {
        person_key: h.person_key, person_name: h.person_name,
        person_entity_id: h.person_entity_id, shared_person_id: h.shared_person_id,
        confidence: h.confidence, is_current: false, seats: [],
      };
      by.set(h.person_key, p);
    }
    p.seats.push(h);
    if (h.is_current && !p.is_current) {
      p.is_current = true;
      p.confidence = h.confidence;
    }
    p.person_entity_id = p.person_entity_id ?? h.person_entity_id;
    p.shared_person_id = p.shared_person_id || h.shared_person_id;
  }
  const people = Array.from(by.values());
  for (const p of people) {
    p.seats.sort((a, b) =>
      Number(b.is_current) - Number(a.is_current)
      || (b.effective_to ?? b.last_seen ?? '').localeCompare(a.effective_to ?? a.last_seen ?? ''));
  }
  return people;
}

function KeyRolesPanel({ keyRoles, filter, onFilter }: {
  keyRoles: { current: KeyRoleHolding[]; former: KeyRoleHolding[] } | undefined;
  filter: RolesFilter;
  onFilter: (f: RolesFilter) => void;
}) {
  const people = groupByPerson([...(keyRoles?.current ?? []), ...(keyRoles?.former ?? [])]);
  const current = people.filter(p => p.is_current);
  const former = people.filter(p => !p.is_current);
  const shown = filter === 'current' ? current : filter === 'former' ? former : people;

  const FILTERS: { key: RolesFilter; label: string; count: number }[] = [
    { key: 'current', label: 'Current', count: current.length },
    { key: 'former', label: 'Former', count: former.length },
    { key: 'all', label: 'All', count: people.length },
  ];

  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-semibold text-grey-900 flex items-center gap-2">
          <Users className="h-4 w-4 text-grey-400" />
          Directors &amp; officers
          <span className="font-normal text-grey-400">
            — one row per person, seats nested; a seat closes when a filing names a successor
          </span>
        </h2>
        <div className="flex items-center gap-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => onFilter(f.key)}
              className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors ${
                filter === f.key
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-grey-500 hover:bg-grey-50'
              }`}
            >
              {f.label} {f.count}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-grey-400">
          {people.length === 0
            ? 'No directors or officers extracted from this entity’s announcements yet.'
            : 'Nobody in this filter.'}
        </p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-100 text-left text-xs text-grey-500">
                <th className="py-2 pr-4">Person</th>
                <th className="py-2 pr-4">Seat</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Named on</th>
                <th className="py-2 pr-4">Latest filing</th>
              </tr>
            </thead>
            {shown.map(p => <KeyRolePersonRows key={p.person_key} person={p} />)}
          </table>
        </TableScroll>
      )}
    </section>
  );
}

function KeyRolePersonRows({ person: p }: { person: KeyRolePerson }) {
  return (
    <tbody className="border-b border-grey-100">
      {p.seats.map((h, i) => (
        <tr key={`${h.person_key}|${h.role}`} className="align-top">
          {i === 0 && (
            <td className="py-2 pr-4" rowSpan={p.seats.length}>
              <div className={`font-medium ${p.is_current ? 'text-grey-900' : 'text-grey-600'}`}>{p.person_name}</div>
              <div className="text-xs text-grey-400">
                {p.person_entity_id ? 'resolved person' : 'name only'}
                {p.confidence ? ` · ${p.confidence}` : ''}
                {p.seats.length > 1 ? ` · ${p.seats.length} seats` : ''}
                {p.shared_person_id && (
                  <span className="ml-1 text-amber-700" title="The resolver bound another director's name to this same person record; shown separately here.">
                    · id shared with another name
                  </span>
                )}
              </div>
            </td>
          )}
          <KeyRoleSeatCells holding={h} />
        </tr>
      ))}
    </tbody>
  );
}

function KeyRoleSeatCells({ holding: h }: { holding: KeyRoleHolding }) {
  const otherLabels = h.labels_seen.filter(l => l !== h.role_label);
  return (
    <>
      <td className="py-2 pr-4">
        <div className={h.is_current ? 'text-grey-800' : 'text-grey-500'}>{h.role_label}</div>
        <div className="text-xs text-grey-400">
          {KEY_ROLE_LABEL[h.role] ?? h.role}
          {otherLabels.length > 0 && ` · earlier: ${otherLabels.join(', ')}`}
        </div>
        {h.closed_seats.map(cs => (
          <div key={`${cs.label}|${cs.effective_to ?? ''}`} className="text-xs text-grey-500 mt-1 pl-2 border-l border-grey-200">
            {cs.label}
          </div>
        ))}
      </td>
      <td className="py-2 pr-4">
        {h.is_current ? (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700">
            {h.change_type === 'incoming' ? 'Incoming' : 'Current'}
          </span>
        ) : (
          <>
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-grey-100 text-grey-600">
              {h.change_type === 'outgoing' ? 'Outgoing' : h.change_type === 'historical' ? 'Historical' : 'Closed'}
              {h.effective_to ? ` ${fmt(h.effective_to)}` : ''}
            </span>
            {h.successor_name && (
              <div className="text-xs text-grey-500 mt-0.5">succeeded by {h.successor_name}</div>
            )}
          </>
        )}
        {/* effective_from is free text as extracted ("1 July 2026", "immediately") — never parsed. */}
        {h.effective_from && (
          <div className="text-xs text-grey-400 mt-0.5">from {h.effective_from}</div>
        )}
        {h.closed_seats.map(cs => (
          <div key={`${cs.label}|${cs.effective_to ?? ''}`} className="text-xs text-grey-500 mt-1">
            closed{cs.effective_to ? ` ${fmt(cs.effective_to)}` : ''}
            {cs.successor_name ? `, succeeded by ${cs.successor_name}` : ''}
          </div>
        ))}
      </td>
      <td className="py-2 pr-4 text-grey-700 whitespace-nowrap">
        {h.filings} {h.filings === 1 ? 'filing' : 'filings'}
        <div className="text-xs text-grey-400">
          {h.first_seen && h.last_seen && h.first_seen !== h.last_seen
            ? `${fmt(h.first_seen)} – ${fmt(h.last_seen)}`
            : fmt(h.last_seen)}
        </div>
      </td>
      <td className="py-2 pr-4 max-w-xs">
        {h.latest_announcement_id ? (
          <Link
            to={`/announcements/${h.latest_announcement_id}`}
            className="text-grey-700 hover:text-primary-700 hover:underline line-clamp-2 text-xs"
          >
            {h.latest_announcement_title ?? '(untitled)'}
          </Link>
        ) : (
          <span className="text-xs text-grey-400">—</span>
        )}
      </td>
    </>
  );
}

// ---------------------------------------------------------------------------
// Panel A · organisational channels
// ---------------------------------------------------------------------------

function ChannelsPanel({ entityId, channels, onChanged }: {
  entityId: string;
  channels: ContactChannel[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);

  const targets = channels.filter(c => c.is_notification_target).length;

  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-semibold text-grey-900 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-grey-400" />
          Organisational channels
          <span className="font-normal text-grey-400">
            — {channels.length} held · {targets} notification target{targets === 1 ? '' : 's'}
          </span>
        </h2>
        <button
          onClick={() => setAdding(a => !a)}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          {adding ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {adding ? 'Cancel' : 'Add channel'}
        </button>
      </div>

      {adding && (
        <AddChannelForm
          entityId={entityId}
          onDone={() => { setAdding(false); onChanged(); }}
        />
      )}

      {channels.length === 0 ? (
        <p className="text-sm text-grey-400">No channels held for this entity.</p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-100 text-left text-xs text-grey-500">
                <th className="py-2 pr-4">Channel</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Notify</th>
                <th className="py-2 pr-4">Emails sent</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Added</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {channels.map(c => (
                <ChannelRow key={c.contact_id} entityId={entityId} channel={c} onChanged={onChanged} />
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </section>
  );
}

function ChannelRow({ entityId, channel, onChanged }: {
  entityId: string;
  channel: ContactChannel;
  onChanged: () => void;
}) {
  const toggle = useMutation({
    mutationFn: () => updateEntityChannel(entityId, channel.contact_id, {
      is_notification_target: !channel.is_notification_target,
    }),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => deleteEntityChannel(entityId, channel.contact_id),
    onSuccess: onChanged,
  });
  const [confirming, setConfirming] = useState(false);

  return (
    <tr className="border-b border-grey-50 align-top">
      <td className="py-2 pr-4 min-w-0">
        <div className="flex items-center gap-1.5 font-mono text-grey-900">
          {channel.channel_email ? <Mail className="h-3.5 w-3.5 text-grey-400 shrink-0" /> : <Phone className="h-3.5 w-3.5 text-grey-400 shrink-0" />}
          <span className="truncate">{channel.channel_email ?? channel.channel_phone}</span>
        </div>
        {(channel.contact_name_raw || channel.channel_email) && channel.channel_phone && (
          <div className="text-xs text-grey-400 pl-5">{channel.channel_phone}</div>
        )}
        {channel.contact_name_raw && (
          <div className="text-xs text-grey-500 pl-5">{channel.contact_name_raw}</div>
        )}
        {channel.suppressed && (
          <span className="ml-5 inline-flex px-1.5 py-0.5 text-xs rounded-full bg-red-50 text-red-700">
            suppressed
          </span>
        )}
      </td>
      <td className="py-2 pr-4 text-grey-600">
        {CHANNEL_TYPE_LABEL[channel.contact_type] ?? channel.contact_type}
      </td>
      <td className="py-2 pr-4">
        <button
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
          title={channel.is_notification_target
            ? 'Receiving notifications — click to stop'
            : 'Not a notification target — click to approve'}
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full transition-colors disabled:opacity-50 ${
            channel.is_notification_target
              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              : 'bg-grey-100 text-grey-500 hover:bg-grey-200'
          }`}
        >
          {toggle.isPending
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : channel.is_notification_target ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          {channel.is_notification_target ? 'Approved' : 'Off'}
        </button>
      </td>
      <td className="py-2 pr-4 text-grey-600">
        {channel.sent_count > 0 ? (
          <span title={`last ${fmt(channel.last_sent)}${channel.last_delivery_status ? ` · ${channel.last_delivery_status}` : ''}`}>
            {channel.sent_count}
            {channel.last_delivery_status && (
              <span className="text-xs text-grey-400"> · {channel.last_delivery_status}</span>
            )}
          </span>
        ) : <span className="text-grey-300">—</span>}
      </td>
      <td className="py-2 pr-4 text-xs text-grey-500">
        {PROVENANCE_LABEL[channel.created_via] ?? channel.created_via}
        {channel.confidence != null && Number(channel.confidence) < 1 && (
          <span className="text-grey-400"> · {Number(channel.confidence).toFixed(2)}</span>
        )}
        {channel.source_announcement_title && (
          <div className="text-grey-400 line-clamp-1" title={channel.source_announcement_title}>
            {channel.source_announcement_title}
          </div>
        )}
      </td>
      <td className="py-2 pr-4 text-xs text-grey-500">{fmt(channel.created_at)}</td>
      <td className="py-2 text-right">
        {confirming ? (
          <span className="inline-flex items-center gap-1.5">
            <button
              onClick={() => { remove.mutate(); setConfirming(false); }}
              className="px-2 py-0.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-2 py-0.5 text-xs text-grey-600 hover:bg-grey-50 rounded-lg"
            >
              Keep
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            className="text-grey-300 hover:text-red-600"
            title="Delete channel"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

function AddChannelForm({ entityId, onDone }: { entityId: string; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState('investor_relations');

  const create = useMutation({
    mutationFn: () => addEntityChannel(entityId, {
      channel_email: email.trim() || null,
      channel_phone: phone.trim() || null,
      contact_type: type,
      contact_name_raw: label.trim() || null,
      is_notification_target: true,
    }),
    onSuccess: onDone,
  });

  return (
    <div className="mb-4 p-3 border border-grey-200 rounded-xl bg-grey-25 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="investors@company.com"
          className="border border-grey-200 rounded-lg px-3 py-2 text-sm sm:col-span-2"
        />
        <input
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="+61 8 9315 0213"
          className="border border-grey-200 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={type}
          onChange={e => setType(e.target.value)}
          className="border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface"
        >
          {CHANNEL_TYPES.map(t => (
            <option key={t} value={t}>{CHANNEL_TYPE_LABEL[t]}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Label, e.g. Investor Enquiries"
          className="flex-1 border border-grey-200 rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || (!email.trim() && !phone.trim())}
          className="px-3 py-2 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {create.isPending ? 'Adding…' : 'Add channel'}
        </button>
      </div>
      {create.isError && (
        <p className="text-xs text-red-600">
          {(create.error as any)?.response?.data?.detail ?? 'Could not add this channel.'}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel B · people
// ---------------------------------------------------------------------------

function PeoplePanel({ entityId, people, filter, onFilter, onOpenPerson, onChanged }: {
  entityId: string;
  people: ContactPerson[];
  filter: PeopleFilter;
  onFilter: (f: PeopleFilter) => void;
  onOpenPerson: (email: string) => void;
  onChanged: () => void;
}) {
  const counts = useMemo(() => ({
    all: people.length,
    approved: people.filter(p => p.approval_status === 'approved').length,
    pending: people.filter(p => p.approval_status !== 'approved' && p.approval_status !== 'rejected').length,
    rejected: people.filter(p => p.approval_status === 'rejected').length,
  }), [people]);

  const shown = people.filter(p => {
    if (filter === 'all') return true;
    if (filter === 'approved') return p.approval_status === 'approved';
    if (filter === 'pending') return p.approval_status !== 'approved' && p.approval_status !== 'rejected';
    return p.approval_status !== 'rejected'; // 'active'
  });

  const FILTERS: { key: PeopleFilter; label: string; count: number }[] = [
    { key: 'active', label: 'Not rejected', count: counts.all - counts.rejected },
    { key: 'pending', label: 'Awaiting review', count: counts.pending },
    { key: 'approved', label: 'Approved', count: counts.approved },
    { key: 'all', label: 'All', count: counts.all },
  ];

  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-semibold text-grey-900 flex items-center gap-2">
          <AtSign className="h-4 w-4 text-grey-400" />
          People
          <span className="font-normal text-grey-400">
            — everyone we have seen on this company's filings, in-house and third-party
          </span>
        </h2>
        <div className="flex items-center gap-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => onFilter(f.key)}
              className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors ${
                filter === f.key
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-grey-500 hover:bg-grey-50'
              }`}
            >
              {f.label} {f.count}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-grey-400">
          {people.length === 0
            ? 'No people extracted from this entity’s announcements yet.'
            : 'Nobody in this filter.'}
        </p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-100 text-left text-xs text-grey-500">
                <th className="py-2 pr-4">Person</th>
                <th className="py-2 pr-4">Organisation</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Notify</th>
                <th className="py-2 pr-4">Emails sent</th>
                <th className="py-2 pr-4">Seen</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map(p => (
                <PersonRow
                  key={p.email}
                  entityId={entityId}
                  person={p}
                  onOpen={() => onOpenPerson(p.email)}
                  onChanged={onChanged}
                />
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </section>
  );
}

function PersonRow({ entityId, person, onOpen, onChanged }: {
  entityId: string;
  person: ContactPerson;
  onOpen: () => void;
  onChanged: () => void;
}) {
  const approve = useMutation({
    mutationFn: (action: PersonApprovalAction) =>
      setPersonApproval(entityId, { email: person.email, action }),
    onSuccess: onChanged,
  });

  const status = person.approval_status ?? 'pending';

  return (
    <tr className="border-b border-grey-50 align-top hover:bg-grey-25">
      <td className="py-2 pr-4 min-w-0">
        <button onClick={onOpen} className="text-left group">
          <div className="font-medium text-grey-900 group-hover:text-primary-700">
            {person.full_name ?? person.email}
          </div>
          <div className="text-xs text-grey-500 font-mono truncate">{person.email}</div>
          {person.job_title && (
            <div className="text-xs text-grey-400">{person.job_title}</div>
          )}
        </button>
        {person.suppressed && (
          <span className="inline-flex mt-0.5 px-1.5 py-0.5 text-xs rounded-full bg-red-50 text-red-700">
            suppressed
          </span>
        )}
      </td>
      <td className="py-2 pr-4 text-grey-700">
        {person.organization ?? <span className="text-grey-300">—</span>}
        {person.other_entity_count > 0 && (
          <button
            onClick={onOpen}
            className="block text-xs text-primary-600 hover:underline"
            title="This person appears on other companies' filings"
          >
            +{person.other_entity_count} other {person.other_entity_count === 1 ? 'company' : 'companies'}
          </button>
        )}
      </td>
      <td className="py-2 pr-4 text-xs text-grey-600">
        {person.role ? (ROLE_LABEL[person.role] ?? person.role) : '—'}
      </td>
      <td className="py-2 pr-4">
        <ApprovalControl
          status={status}
          busy={approve.isPending}
          onAction={a => approve.mutate(a)}
        />
        {person.reviewed_by && (
          <div className="text-xs text-grey-400 mt-0.5" title={`reviewed ${fmt(person.reviewed_at)}`}>
            {person.reviewed_by.split('@')[0]} · {fmt(person.reviewed_at, 'd MMM')}
          </div>
        )}
      </td>
      <td className="py-2 pr-4 text-grey-600">
        {person.sent_count > 0 ? (
          <span title={`last ${fmt(person.last_sent)}`}>
            {person.sent_count}
            {person.last_delivery_status && (
              <span className="text-xs text-grey-400"> · {person.last_delivery_status}</span>
            )}
          </span>
        ) : <span className="text-grey-300">—</span>}
      </td>
      <td className="py-2 pr-4 text-xs text-grey-500">
        {fmt(person.first_seen, 'd MMM yy')} → {fmt(person.last_seen, 'd MMM yy')}
        <div className="text-grey-400">
          {person.sightings} sighting{person.sightings === 1 ? '' : 's'} · {person.announcements} filing{person.announcements === 1 ? '' : 's'}
        </div>
      </td>
      <td className="py-2 text-right">
        <button
          onClick={onOpen}
          className="inline-flex items-center gap-0.5 text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          Open <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

export function ApprovalControl({ status, busy, onAction }: {
  status: string;
  busy: boolean;
  onAction: (action: PersonApprovalAction) => void;
}) {
  if (busy) {
    return <Loader2 className="h-3.5 w-3.5 text-grey-400 animate-spin" />;
  }
  if (status === 'approved') {
    return (
      <button
        onClick={() => onAction('reject')}
        title="Approved for notifications — click to reject"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
      >
        <Check className="h-3 w-3" /> Approved
      </button>
    );
  }
  if (status === 'rejected') {
    return (
      <button
        onClick={() => onAction('reset')}
        title="Rejected — click to re-open for review"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-grey-100 text-grey-500 hover:bg-grey-200"
      >
        <X className="h-3 w-3" /> Rejected
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={() => onAction('approve')}
        className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-50 text-primary-700 hover:bg-primary-100"
      >
        Approve
      </button>
      <button
        onClick={() => onAction('reject')}
        className="px-2 py-0.5 text-xs font-medium rounded-full text-grey-500 hover:bg-grey-100"
      >
        Reject
      </button>
    </span>
  );
}
